<template>
  <div :class="cn('tw:block', $attrs.class as any)" :style="$attrs.style as any">
    <label
      v-if="label != null"
      :id="labelId"
      :for="inputId"
      class="tw:mb-1.5 tw:block tw:text-13 tw:font-medium tw:text-gray-700">
      <span v-html="label"></span>
      <span v-if="required" aria-hidden="true" class="tw:text-red-600">&nbsp;*</span>
    </label>

    <!-- bare control (no affixes) -->
    <input
      v-if="!hasAffix"
      :id="inputId"
      ref="inputRef"
      v-bind="forwardedAttrs"
      :type="resolvedType"
      :value="modelValue"
      :disabled="disabled"
      :aria-invalid="invalid || undefined"
      :aria-describedby="messageId"
      :class="cn(CONTROL_BASE, invalid && CONTROL_INVALID, inputClassName)"
      @input="onInput"
      @change="onChange" />

    <!-- affix wrapper -->
    <div v-else :class="cn(GROUP_BASE, invalid && GROUP_INVALID, disabled && GROUP_DISABLED)">
      <span v-if="$slots.iconStart" class="tw:flex tw:shrink-0 tw:items-center tw:text-gray-400">
        <slot name="iconStart" />
      </span>
      <component
        v-else-if="leadingIcon"
        :is="leadingIcon"
        :size="16"
        :class="['tw:shrink-0 tw:text-gray-400', leadingIconClassName]" />
      <span v-if="$slots.prefix" :class="AFFIX_PREFIX">
        <slot name="prefix" />
      </span>

      <input
        :id="inputId"
        ref="inputRef"
        v-bind="forwardedAttrs"
        :type="resolvedType"
        :value="modelValue"
        :disabled="disabled"
        :aria-invalid="invalid || undefined"
        :aria-describedby="messageId"
        :class="cn(GROUP_CONTROL, disabled && 'tw:text-inherit', inputClassName)"
        @input="onInput"
        @change="onChange" />

      <span v-if="$slots.suffix" :class="AFFIX_SUFFIX">
        <slot name="suffix" />
      </span>
      <button
        v-if="isRevealable"
        type="button"
        class="tw:flex tw:shrink-0 tw:cursor-pointer tw:items-center tw:border-none tw:bg-transparent tw:p-0 tw:text-gray-400 tw:hover:text-gray-700"
        :aria-label="isRevealed ? $t('form.password.hide') : $t('form.password.show')"
        @click="isRevealed = !isRevealed">
        <component :is="isRevealed ? IconEyeOff : IconEye" :size="16" />
      </button>
      <span v-if="$slots.iconEnd" class="tw:flex tw:shrink-0 tw:items-center tw:text-gray-400">
        <slot name="iconEnd" />
      </span>
      <component
        v-else-if="trailingIcon"
        :is="trailingIcon"
        :size="16"
        :class="['tw:shrink-0 tw:text-gray-400', trailingIconClassName]" />
    </div>

    <!-- error takes precedence over hint; several errors render as a list -->
    <Transition name="lead-message" mode="out-in">
      <div v-if="errorList.length > 0" :id="messageId" class="tw:mt-1.5 tw:text-13 tw:font-normal tw:text-red-600">
        <span v-if="errorList.length === 1">{{ errorList[0] }}</span>
        <ul v-else class="tw:list-disc tw:pl-4">
          <li v-for="error in errorList" :key="error">{{ error }}</li>
        </ul>
      </div>
      <span
        v-else-if="$slots.hint"
        :id="messageId"
        class="tw:mt-1.5 tw:block tw:text-13 tw:font-normal tw:text-gray-600">
        <slot name="hint" />
      </span>
    </Transition>
  </div>
</template>

<script setup lang="ts">
// LeadInput — mirrors the LEAD Design System <Input> + <Field> composition.
// shared control geometry lives in ./controlStyles.ts, taken from the DS's controlStyles.ts
//
// Kept API-compatible with the legacy LeadInput: label, placeholder, errors[],
// #prefix / #suffix / #hint slots, v-model, class/style passthrough.
// leadingIcon / trailingIcon props plus #iconStart / #iconEnd slots; the slot wins for the same position.
// Password reveal (type="password", on unless `revealable` is false) is a local addition, not in the DS.
// Not supported: type="switch" (use LeadSwitch) and type="textarea" (use LeadTextarea).
import type { Component } from 'vue';
import { computed, ref, useAttrs, useId } from 'vue';
import { IconEye, IconEyeOff } from '@tabler/icons-vue';
import { cn } from '@/lib/cn';
import {
  AFFIX_PREFIX,
  AFFIX_SUFFIX,
  CONTROL_BASE,
  CONTROL_INVALID,
  GROUP_BASE,
  GROUP_CONTROL,
  GROUP_DISABLED,
  GROUP_INVALID,
} from './controlStyles';

defineOptions({ inheritAttrs: false });

interface Props {
  modelValue?: string | number | null;
  label?: string;
  errors?: string[] | string | null;
  required?: boolean;
  disabled?: boolean;
  type?: string;
  leadingIcon?: Component;
  trailingIcon?: Component;
  leadingIconClassName?: string;
  trailingIconClassName?: string;
  inputClassName?: string;
  /** show a reveal toggle on a password input; defaults on for `type="password"` */
  revealable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  label: undefined,
  errors: null,
  required: false,
  disabled: false,
  type: 'text',
  leadingIcon: undefined,
  trailingIcon: undefined,
  leadingIconClassName: '',
  trailingIconClassName: '',
  inputClassName: '',
  // must stay undefined: Vue would cast an absent boolean to false and break the password default
  revealable: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number];
  change: [event: Event];
}>();

const attrs = useAttrs();
const slots = defineSlots<{
  prefix?: () => any;
  suffix?: () => any;
  iconStart?: () => any;
  iconEnd?: () => any;
  hint?: () => any;
}>();

/* --------------------------------- STATE ---------------------------------- */

const inputRef = ref<HTMLInputElement | null>(null);
const generatedId = useId();
const inputId = computed(() => (attrs.id as string) || `lead-input-${generatedId}`);
const labelId = computed(() => (props.label != null ? `${inputId.value}-label` : undefined));
const isRevealed = ref(false);

/* --------------------------------- COMPUTED ------------------------------- */

// class/style are consumed by the wrapper — everything else falls through to <input>.
const forwardedAttrs = computed(() => {
  const { class: _c, style: _s, id: _i, ...rest } = attrs as any;
  return rest;
});

const errorList = computed<string[]>(() => {
  if (props.errors == null) return [];
  return Array.isArray(props.errors) ? props.errors.filter(Boolean) : [props.errors].filter(Boolean);
});
const invalid = computed(() => errorList.value.length > 0);

const messageId = computed(() => {
  if (errorList.value.length > 0 || slots.hint) return `${inputId.value}-message`;
  return undefined;
});

const isRevealable = computed(() => props.revealable ?? props.type === 'password');

// a revealed password shows its characters
const resolvedType = computed(() => (isRevealable.value && isRevealed.value ? 'text' : props.type));

const hasAffix = computed(
  () =>
    !!slots.prefix ||
    !!slots.suffix ||
    !!slots.iconStart ||
    !!slots.iconEnd ||
    !!props.leadingIcon ||
    !!props.trailingIcon ||
    isRevealable.value
);

/* --------------------------------- METHODS -------------------------------- */

const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  // preserve v-model.number semantics: emit number for numeric types
  const raw = target.value;
  if (props.type === 'number') {
    emit('update:modelValue', raw === '' ? '' : Number(raw));
  } else {
    emit('update:modelValue', raw);
  }
};

const onChange = (event: Event) => emit('change', event);

defineExpose({ focus: () => inputRef.value?.focus(), inputRef });
</script>

<style scoped>
.lead-message-enter-active,
.lead-message-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.lead-message-enter-from,
.lead-message-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (prefers-reduced-motion: reduce) {
  .lead-message-enter-active,
  .lead-message-leave-active {
    transition: none;
  }
}
</style>
